import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  AuthFlowRoutesService,
  AuthRedirectService,
  AuthRedirectStorageService,
  RoutingService,
} from '@spartacus/core';
import { take } from 'rxjs/operators';
import { B2bunitService } from '../b2bunit/b2bunit.service';
import { LanguageExtendService } from '../language/language-extend.service';
import { StorageService } from '../storage/storage.service';
@Injectable({
  providedIn: 'root',
})
export class ShimanoAuthRedirectService extends AuthRedirectService {
  private ignoredUrls = new Set<string>();
  private lastAuthGuardNavigation: {
    url: string;
    navigationId: number;
  };
  asmEnabled = this.storageService.getLocalStorageItem('asm_enabled');
  constructor(
    routing: RoutingService,
    router: Router,
    authRedirectStorageService: AuthRedirectStorageService,
    authFlowRoutesService: AuthFlowRoutesService,
    private b2bUnitService: B2bunitService,
    private languageExtendService: LanguageExtendService,
    private storageService: StorageService
  ) {
    super(routing, router, authRedirectStorageService, authFlowRoutesService);
  }

  // Save redirectUrl to the local storage
  protected override init(): void {
    this.subscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;

        if (this.shouldIgnoreRedirectUrl(url)) {
          return;
        }

        this.setRedirectUrl(url);
      }
    });
  }

  private shouldIgnoreRedirectUrl(url: string): boolean {
    return ['unit-selection', 'dealer-selection', 'callback', 'global', 'error='].some((excluded) =>
      url.includes(excluded)
    );
  }
  override redirect(): void {
    this.authRedirectStorageService
      .getRedirectUrl()
      .pipe(take(1))
      .subscribe((redirectUrl) => {
        this.b2bUnitService.getShimanoUnitSelectionInfo$().subscribe((b2bunitEnabled: any) => {
          const shimanoSessionB2BUnit_enabled = b2bunitEnabled === 'true';
          if (shimanoSessionB2BUnit_enabled && !this.asmEnabled) {
            this.b2bUnitService.getCurrentUserB2BUnits().subscribe((userUnit: any) => {
              let getShimanoSessionB2BUnit = this.storageService.getLocalStorageItem('shimanoSessionB2BUnit');
              let b2bunitsInfo = getShimanoSessionB2BUnit ? true : false;
              if (userUnit && userUnit.b2bUnits.length > 1) {
                this.storageService.setLocalStorageitem('shimanoSessionB2BUnit_multiple', 'true');
                if (b2bunitsInfo) {
                  this.nextLandingDestination(redirectUrl);
                } else {
                  const lastAuthTime = this.storageService.getLocalStorageItem('lastAuthTime');
                  const fiftyMinutes = 50 * 60 * 1000;
                  const timeSinceLastAuth = lastAuthTime ? Date.now() - parseInt(lastAuthTime, 10) : 0;
                  const lang = this.storageService.getLocalStorageItem('selectedLanguageOnLogin');
                  this.languageExtendService.setShimanoActiveLanguage(lang).pipe(take(1)).subscribe();
                  this.routing.go('/unit-selection');
                  if (timeSinceLastAuth > fiftyMinutes) {
                    // More than 50 minutes for Idle white page scenario
                    window.location.href = this.router.serializeUrl(this.router.createUrlTree(['/unit-selection']));
                  }
                }
              } else {
                this.storageService.setLocalStorageitem('shimanoSessionB2BUnit_multiple', 'false');
                this.nextLandingDestination(redirectUrl);
              }
            });
          } else {
            this.nextLandingDestination(redirectUrl);
          }
        });
      });
  }

  nextLandingDestination(url: any) {
    const lastAuthTime = this.storageService.getLocalStorageItem('lastAuthTime');
    const fiftyMinutes = 50 * 60 * 1000;
    const timeSinceLastAuth = lastAuthTime ? Date.now() - parseInt(lastAuthTime, 10) : 0;
    if (!url) {
      const storedOriginalRedirectUrl = this.storageService.getLocalStorageItem('originalRedirectUrl');
      if (storedOriginalRedirectUrl) {
        url = storedOriginalRedirectUrl; // This stores the last page URL after logout
        this.storageService.removeLocalStorageItem('originalRedirectUrl');
      }
    }

    if (url === undefined) {
      this.router.navigate(['/']);
    } else {
      this.routing.goByUrl(this.cleanRedirectUrl(url)); // if page is disabled or doesnt exist / custom-auth.guard fallback
    }

    if (timeSinceLastAuth > fiftyMinutes) {
      // More than 50 minutes for Idle white page scenario
      window.location.href = this.cleanRedirectUrl(url);
    }
    this.authRedirectStorageService.setRedirectUrl(undefined);
    this.lastAuthGuardNavigation = undefined;
  }

  reportAuthGuard() {
    const { url, navigationId } = this.getCurrentNavigation();
    this.lastAuthGuardNavigation = { url, navigationId };
    if (!this.shouldIgnoreRedirectUrl(url)) {
      this.authRedirectStorageService.setRedirectUrl(url);
    }
  }

  reportNotAuthGuard() {
    const { url, initialUrl, navigationId } = this.getCurrentNavigation();

    this.ignoredUrls.add(url);

    // Don't save redirect url if you've already come from page with NotAuthGuard (i.e. user has come from login to register)
    if (!this.ignoredUrls.has(initialUrl)) {
      // We compare the navigation id to find out if the url cancelled by AuthGuard (i.e. my-account) is more recent
      // than the last opened page
      if (!this.lastAuthGuardNavigation || this.lastAuthGuardNavigation.navigationId < navigationId - 1) {
        if (!this.isStorefrontContextRootUrl(initialUrl) && !this.shouldIgnoreRedirectUrl(url)) {
          this.authRedirectStorageService.setRedirectUrl(initialUrl);
        }
        this.lastAuthGuardNavigation = undefined;
      }
    }
  }

  private isStorefrontContextRootUrl(url: string): boolean {
    if (!url) {
      return false;
    }

    const cleanUrl = url.split('?')[0].split('#')[0];
    const parts = cleanUrl.split('/').filter(Boolean);

    return parts.length === 3;
  }

  private getCurrentNavigation(): {
    navigationId: number;
    url: string;
    initialUrl: string;
  } {
    const initialUrl = this.router.url;
    const navigation = this.router.getCurrentNavigation();
    const url = this.router.serializeUrl(navigation.finalUrl);
    return {
      navigationId: navigation.id,
      url,
      initialUrl,
    };
  }

  // remove suffix '/login' for redirectUrl
  private cleanRedirectUrl(redirectUrl: string): string {
    let newRedirectUrl = redirectUrl;
    const loginPath = '/login';
    if (redirectUrl.endsWith(loginPath)) {
      newRedirectUrl = redirectUrl.slice(0, -loginPath.length);
    }
    return newRedirectUrl;
  }
}
